import React, { useState, useEffect, useRef } from 'react';
import { User, FileText, Image as ImageIcon, Table, ThumbsUp, ThumbsDown, Music, Video, Bookmark, Trash2, Volume2, Loader2, Download, Share2, Maximize2, RefreshCw, Link2, Check, X } from 'lucide-react';
import ShareMediaModal from './ShareMediaModal';
import MediaPlayerLightbox from './MediaPlayerLightbox';
import { StorageUpgradeCard, AgentTipNote } from './messageNoticeCards';
import { JsonRenderer } from '../../spikes/generativeUI';
import type { UISpec } from '../../spikes/generativeUI/uiSpec';
import { TopUpNotice } from '../wallet/TopUpCard';
import Tooltip from '../ui/Tooltip';
import { ConfirmSpendCard, isConfirmSpendSpec, type ConfirmSpendSpec, type ConfirmSpendEdits } from './ConfirmSpendCard';
import { submitContentFeedback, translateConversationMessage } from '../../services/conversationService';
import { resolveDownloadUrl, fetchAsBlob } from '../../services/storageService';
import { useOptionalToast } from '../../contexts/NotificationContext';
import { useWallet } from '../../contexts/WalletContext';
import { Link } from 'react-router-dom';
import PromoCard from './PromoCard';
import MemberAvatar, { useStorageUrl } from './MemberAvatar';
import MediaArtCard from './MediaArtCard';
import VoiceMessageBubble from './VoiceMessageBubble';
import MessageReactions from './MessageReactions';
import { formatBytes } from '../../utils/chatAttachments';
import Markdown from './Markdown';
import type { ChatMsg, MemberSpec } from './types';
import { TYPE } from '../../constants/typeScale';

// A valid, empty (silent) WAV used to "unlock" an <audio> element inside a click gesture, so a later
// play() with a URL fetched asynchronously isn't blocked by the browser autoplay policy.
const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/**
 * READ-ALOUD control — HIDDEN (owner 2026-08-22: "remove speech button for chat message, no need" →
 * clarified as "just hide it"). Only the CONTROL is hidden: the speak handler, the `speakConversationMessage`
 * route, and the charge path are all untouched, and a message that already has a voice-over still plays via
 * VoiceMessageBubble (replays are free). Flip this one constant back to true to restore the button.
 * Typed as `boolean` so the surrounding code stays honestly boolean rather than narrowing to `false`.
 */
const READ_ALOUD_ENABLED: boolean = false;

/** WP per ~250-character block of read-aloud — mirrors the backend `audio` capability cost, used only to
 *  size the read-aloud spend CONFIRMATION. The server is the authority and charges on success. */
const VOICE_WP_PER_BLOCK = 80;

const ATT_ICON = { document: FileText, image: ImageIcon, data: Table, audio: Music, video: Video } as const;
const INLINE_MEDIA = ['image', 'audio', 'video'];

/** Specs the chat may carry that aren't generative-UI catalog nodes (rendered specially). */
const isTopUpSpec = (spec: unknown): boolean => !!spec && (spec as any).type === 'TopUp';
const isPromoSpec = (spec: unknown): boolean => !!spec && (spec as any).type === 'Promo';
const isAgentTipSpec = (spec: unknown): boolean => !!spec && (spec as any).type === 'AgentTip';
const isStorageUpgradeSpec = (spec: unknown): boolean => !!spec && (spec as any).type === 'StorageUpgrade';

/** A short, human label for a web citation: the source title if present, else the bare domain. */
const citationLabel = (c: { title?: string; url: string }): string => {
  const t = (c.title || '').trim();
  if (t && !/^https?:\/\//i.test(t)) return t.length > 40 ? `${t.slice(0, 40)}…` : t;
  try { return new URL(c.url).hostname.replace(/^www\./, ''); } catch { return c.url.slice(0, 30); }
};

const THREE_DAYS_MS = 3 * 86_400_000;

/**
 * Send-time label. Timestamps are stored in UTC; `ts` is an epoch, so toLocale* renders
 * in the VIEWER's local timezone automatically. Within 3 days we show a relative
 * "<length> ago"; beyond that, an absolute local date+time (with year if not this year).
 * The full local datetime is always available on hover (the element's `title`).
 */
const formatMsgTime = (ts?: number): string => {
  if (!ts) return '';
  const d = new Date(ts);
  const age = Date.now() - ts;
  if (age >= 0 && age < THREE_DAYS_MS) {
    const s = Math.floor(age / 1000);
    if (s < 45) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  return `${date}, ${time}`;
};

/**
 * Renders an uploaded/generated media attachment inline — image (`<img>`), video
 * (`<video controls>`), or music/audio (`<audio controls>`). A Drive-backed chat resolves
 * via the backend media proxy (`drive_file_id`); a cloud chat resolves the Firebase Storage
 * path. Firebase SDK is lazy-loaded at runtime (importing it at module top pulls in
 * `undici`, which the jsdom test transform can't parse).
 */
const AttachmentMedia: React.FC<{ kind: string; path?: string | null; posterPath?: string | null; lyrics?: string | null; driveFileId?: string | null; conversationId?: string; name: string; onExpand?: (url: string, kind: string, name: string, posterUrl?: string | null, lyrics?: string | null, editablePath?: string | null) => void }>
  = ({ kind, path, posterPath, lyrics, driveFileId, conversationId, name, onExpand }) => {
  const [url, setUrl] = useState<string | null>(null);
  const posterUrl = useStorageUrl(posterPath); // generated cover (storage path) for songs/videos
  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    (async () => {
      try {
        if (driveFileId && conversationId) {
          const { fetchDriveMediaUrl } = await import('../../services/conversationMediaService');
          objectUrl = await fetchDriveMediaUrl(conversationId, driveFileId);
          if (alive) setUrl(objectUrl); else URL.revokeObjectURL(objectUrl);
          return;
        }
        if (!path) return;
        const u = await resolveDownloadUrl(path);
        if (alive) setUrl(u);
      } catch { /* object gone / no access */ }
    })();
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [path, driveFileId, conversationId]);

  // A small expand affordance over the media → opens the full-screen player. For image we ALSO make
  // the media itself tap-to-open (zoom cursor); video/audio keep their own controls, so only the icon opens it.
  // Only a storage-backed image (an upload or a chat-media asset) is editable — a Drive-backed asset has no
  // editable storage path, so the editor is offered only when `path` is present.
  const editablePath = kind === 'image' && path ? path : null;
  const expand = url && onExpand ? () => onExpand(url, kind, name, posterUrl, lyrics, editablePath) : undefined;
  const ExpandBtn = expand ? (
    <button type="button" onClick={(e) => { e.stopPropagation(); expand(); }} aria-label="Expand" title="Expand"
      className="absolute top-1.5 right-1.5 rounded-md bg-black/45 p-1 text-white/85 hover:text-white hover:bg-black/65">
      <Maximize2 className="w-3.5 h-3.5" />
    </button>
  ) : null;

  if (kind === 'audio') {
    if (!url) return <div className="w-72 h-72 rounded-xl bg-secondary-100 dark:bg-secondary-800 animate-pulse" />;
    // Song card: generated cover ON TOP, audio player BELOW (the message's action icons sit under it).
    return <MediaArtCard mediaUrl={url} posterUrl={posterUrl} name={name} onExpand={expand ? () => expand() : undefined} />;
  }
  if (kind === 'video') {
    if (!url) return <div className="w-48 h-32 rounded-lg bg-secondary-100 dark:bg-secondary-800 animate-pulse" />;
    return (
      <div className="relative inline-block">
        <video controls src={url} poster={posterUrl || undefined} className="max-w-[18rem] max-h-80 rounded-lg border border-secondary-200 dark:border-secondary-600" data-testid="att-video" />
        {ExpandBtn}
      </div>
    );
  }
  if (!url) return <div className="w-48 h-32 rounded-lg bg-secondary-100 dark:bg-secondary-800 animate-pulse" />;
  return (
    <div className="relative inline-block group">
      <img src={url} alt={name} onClick={expand}
        className={`max-w-[18rem] max-h-80 rounded-lg border border-secondary-200 dark:border-secondary-600 ${expand ? 'cursor-zoom-in' : ''}`} />
      {ExpandBtn}
    </div>
  );
};

/** Attachments under a message: image/audio/video render inline; documents/data render as chips. */
const AttachmentBlock: React.FC<{ msg: ChatMsg; tone: 'own' | 'other'; conversationId?: string }> = ({ msg, tone, conversationId }) => {
  const [lightbox, setLightbox] = useState<{ url: string; kind: string; name: string; posterUrl?: string | null; lyrics?: string | null; editablePath?: string | null } | null>(null);
  if (!msg.attachments?.length) return null;
  const media = msg.attachments.filter((a) => INLINE_MEDIA.includes(a.kind) && (a.storage_path || a.drive_file_id));
  const files = msg.attachments.filter((a) => !INLINE_MEDIA.includes(a.kind) || !(a.storage_path || a.drive_file_id));
  const chip = tone === 'own' ? 'bg-white/15 text-white' : 'bg-secondary-200 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-200';
  const openPlayer = (url: string, kind: string, name: string, posterUrl?: string | null, lyrics?: string | null, editablePath?: string | null) => setLightbox({ url, kind, name, posterUrl, lyrics, editablePath });
  return (
    <div className={`flex flex-col gap-1.5 ${tone === 'own' ? 'items-end' : ''}`} data-testid="msg-attachments">
      {lightbox && <MediaPlayerLightbox url={lightbox.url} kind={lightbox.kind} name={lightbox.name} posterUrl={lightbox.posterUrl} lyrics={lightbox.lyrics} editablePath={lightbox.editablePath} conversationId={conversationId} onClose={() => setLightbox(null)} />}
      {media.map((a, i) => <AttachmentMedia key={`m${i}`} kind={a.kind} path={a.storage_path} posterPath={a.poster_path} lyrics={a.lyrics} driveFileId={a.drive_file_id} conversationId={conversationId} name={a.name} onExpand={openPlayer} />)}
      {files.length > 0 && (
        <div className={`flex flex-wrap gap-1.5 ${tone === 'own' ? 'justify-end' : ''}`}>
          {files.map((a, i) => {
            const Icon = ATT_ICON[a.kind] || FileText;
            return (
              <span key={`f${i}`} className={`inline-flex items-center gap-1 max-w-[12rem] px-2 py-1 rounded-lg ${TYPE.meta} ${chip}`}>
                <Icon className="w-3 h-3 shrink-0" />
                <span className="truncate">{a.name}</span>
                <span className="opacity-70 shrink-0">{formatBytes(a.size)}</span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * A single chat message. Specialist messages can carry a generative-UI `spec` that
 * renders inline from the catalog. In multi-human chats, only YOUR messages are
 * right-aligned; another participant's message renders on the left with their name.
 */
const MessageBubble: React.FC<{
  msg: ChatMsg;
  onAction?: (action: string, value?: string) => void;
  myUid?: string;
  /** Live roster member matching this agent message (for current alias/avatar). */
  member?: MemberSpec | null;
  /** Admin catalog default avatar image for this agent (fallback below a per-chat avatar). */
  avatarImage?: string | null;
  /** Tap the agent's avatar to open its detail/edit panel. */
  onSelectAgent?: (member: MemberSpec) => void;
  /** The chat id — needed to resolve Drive-backed media via the proxy. */
  conversationId?: string;
  /** The prompt that produced this message (nearest preceding user turn) — for feedback. */
  promptText?: string;
  /** Toggle the caller's emoji reaction on this message. */
  onReact?: (emoji: string) => void;
  /** Owner-only: bookmark/unbookmark this message. */
  onBookmark?: (bookmarked: boolean) => void;
  /** Owner-only: delete this message. */
  onDelete?: () => void;
  /** Recommendation tip — add the suggested catalog agent to the group (owner). */
  onAddAgent?: (agentId: string) => Promise<void> | void;
  /** Recommendation tip — consult the suggested agent once (guest reply). */
  onConsultAgent?: (agentId: string) => Promise<void> | void;
  /** Spend-confirm card — "Make it" resumes the gated generation. */
  onConfirmSpend?: (spec: ConfirmSpendSpec, dontAskAgain: boolean, edits: ConfirmSpendEdits) => Promise<void> | void;
  /** Regenerate a generated media message — re-runs the prompt (goes through the spend-confirm). */
  onRegenerate?: (prompt: string) => Promise<void> | void;
  /** Roster names in this chat — a bolded name in the text becomes a clickable mention. */
  memberNames?: string[];
  /** Clicking a matched agent name drops its `@handle` into the composer. */
  onMention?: (name: string) => void;
}> = ({ msg, onAction, myUid, member, avatarImage, onSelectAgent, conversationId, promptText, onReact, onBookmark, onDelete, onAddAgent, onConsultAgent, onConfirmSpend, onRegenerate, memberNames, onMention }) => {
  const { showError } = useOptionalToast();
  const { wp } = useWallet();
  // One persistent <audio> element per bubble, reused across plays so it can be "unlocked" inside the
  // click gesture (browsers block play() that happens after an async gap — see speak()).
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Stop any read-aloud playback if the bubble unmounts (chat switch / message delete) — otherwise the
  // orphaned <audio> keeps playing with no UI left to pause it.
  useEffect(() => () => { try { audioRef.current?.pause(); audioRef.current = null; } catch { /* ignore */ } }, []);
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);
  const [speaking, setSpeaking] = useState(false);
  const [confirmingSpeak, setConfirmingSpeak] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // In-bubble translate-back (Instagram/X pattern): only for non-English messages; cached on the message.
  const [txText, setTxText] = useState<string>(msg.translations?.en || '');
  // Translated generative-UI spec (the choice chips/labels), so "See translation" also covers the
  // interactive choices under the bubble — not just the prose. Seeded from the cached copy if present.
  const parseCachedSpec = (): unknown => {
    try { return msg.spec_translations?.en ? JSON.parse(msg.spec_translations.en) : null; } catch { return null; }
  };
  const [txSpec, setTxSpec] = useState<unknown>(parseCachedSpec);
  const [txOpen, setTxOpen] = useState(false);
  const [txLoading, setTxLoading] = useState(false);
  const canTranslate = !!msg.language && msg.language !== 'en';
  const translate = async () => {
    if (txText || txSpec) { setTxOpen(true); if (txText) return; }
    if (!conversationId) return;
    setTxLoading(true);
    try {
      const r = await translateConversationMessage(conversationId, msg.id);
      if (r.translation) setTxText(r.translation);
      if (r.spec) setTxSpec(r.spec);
      if (r.translation || r.spec) setTxOpen(true);
    }
    catch { /* best-effort — leave the original */ } finally { setTxLoading(false); }
  };
  const fromHuman = msg.author === 'you';
  const mine = fromHuman && (!msg.authorUid || !myUid || msg.authorUid === myUid);

  // System notices (e.g. a failed/empty reply) render as a centered muted line; a
  // system message may also carry a spec (e.g. an exported artifact card).
  if (msg.author === 'system') {
    // An "add this specialist" tip renders as its own subtle monochrome note (bold name), not a
    // catalog spec — so it never falls through to the Unsupported fallback.
    if (isAgentTipSpec(msg.spec)) {
      return (
        <div className="flex flex-col items-center" data-testid="system-msg">
          <AgentTipNote spec={msg.spec as any} content={msg.content} onAdd={onAddAgent} onConsult={onConsultAgent} />
        </div>
      );
    }
    // Out-of-credits → a small, toast-like "See plans" pill (NOT a big central card). The pill
    // carries the message itself, so we skip the heavy centered paragraph for this case.
    if (isTopUpSpec(msg.spec)) {
      return (
        <div data-testid="system-msg">
          <TopUpNotice />
        </div>
      );
    }
    return (
      <div className="space-y-2 flex flex-col items-center">
        <p className={`text-center ${TYPE.control} text-secondary-400`} data-testid="system-msg">{msg.content}</p>
        {isStorageUpgradeSpec(msg.spec)
          ? <StorageUpgradeCard />
          : isPromoSpec(msg.spec)
          ? <PromoCard spec={msg.spec as any} />
          : msg.spec ? <JsonRenderer spec={msg.spec as UISpec} onAction={onAction} /> : null}
      </div>
    );
  }

  if (mine) {
    return (
      <div className="flex flex-col items-end gap-1.5" data-testid="msg-own">
        {/* 13px is a DELIBERATE exception to the type scale, not drift (owner 2026-08-22): chat body copy is
            set one step below the scale's 14px body on both bubbles and in the composer, so what you type
            matches what you read. Leave it — a sweep that "corrects" it to TYPE.body breaks that pairing. */}
        <div className="max-w-[75%] rounded-2xl rounded-br-sm px-4 py-2.5 bg-secondary-900 dark:bg-white text-white dark:text-secondary-900 text-[13px] leading-relaxed whitespace-pre-wrap">
          {msg.content}
        </div>
        <AttachmentBlock msg={msg} tone="own" conversationId={conversationId} />
        {onReact && <MessageReactions reactions={msg.reactions} myId={myUid} align="end" onToggle={onReact} />}
        {msg.ts ? <span className={`${TYPE.meta} text-secondary-400`} title={new Date(msg.ts).toLocaleString()}>{formatMsgTime(msg.ts)}</span> : null}
      </div>
    );
  }

  const canSelect = !fromHuman && member && onSelectAgent;
  const displayName = (member?.alias || member?.name) ?? msg.authorName;
  // The agent's LENS (its analytical dimension) — the persona IS the lens ("Macro Analyst" -> "Macro"). Shown as a
  // subtle chip on each agent message so the multi-dimensional structure is legible, not a chat blob.
  const lens = (() => {
    if (fromHuman) return null;
    const p = (member?.persona || '').trim();
    if (!p || p === 'Person') return null;
    return p.replace(/\s*analysts?$/i, '').trim() || p;
  })();

  // Feedback applies to an agent message that produced media/an artifact (doc/image/media).
  const isArtifact = !!(msg.spec && (msg.spec as any).type === 'Artifact');
  const hasMedia = !!msg.attachments?.some((a) => a.storage_path || a.drive_file_id);
  const feedbackKind: 'image' | 'document' | 'media' | 'artifact' | null = !fromHuman
    ? (isArtifact ? 'artifact'
      : msg.attachments?.some((a) => a.kind === 'image') ? 'image'
      : msg.attachments?.some((a) => a.kind === 'audio' || a.kind === 'video') ? 'media'
      : hasMedia ? 'document' : null)
    : null;
  const sendFeedback = (value: 'up' | 'down') => {
    setFeedback(value);   // optimistic; fire-and-forget (logging only)
    if (!conversationId || !feedbackKind) return;
    submitContentFeedback({ conversationId, messageId: msg.id, feedback: value, contentKind: feedbackKind, prompt: promptText })
      .catch(() => { /* feedback is best-effort */ });
  };
  // Agent (non-human) text messages can be read aloud in the agent's voice.
  const canSpeak = READ_ALOUD_ENABLED && !fromHuman && !!conversationId && !!msg.content?.trim();
  // Estimated credits to read this message aloud — scales with length (~1 block per 250 chars, capped at 4),
  // mirroring the backend voiceUnits. This is a CONSENT figure shown at the moment of spending (kept, unlike the
  // ambient price tags we removed); the wallet is charged on success server-side.
  const voiceWpEstimate = Math.min(4, Math.max(1, Math.ceil((msg.content?.length || 0) / 250))) * VOICE_WP_PER_BLOCK;
  // Proactively guard the action: when the wallet can't cover the estimate, don't attempt (and fail into a
  // toast) — show the top-up prompt in the confirm bar instead. The server stays the source of truth (it
  // charges on success), so this is a friendly pre-check, not the enforcement.
  const canAffordVoice = wp >= voiceWpEstimate;
  const VOICE_CONFIRM_OFF = 'wt_voice_confirm_off';
  // A first click asks for confirmation (spending credits) unless the user chose "don't ask again".
  const onSpeakClick = () => {
    // Broke → always surface the confirm bar (top-up state), even if "don't ask again" was set.
    if (!canAffordVoice) { setConfirmingSpeak(true); return; }
    let off = false;
    try { off = localStorage.getItem(VOICE_CONFIRM_OFF) === '1'; } catch { /* ignore */ }
    if (off) { void speak(); } else { setConfirmingSpeak(true); }
  };
  const confirmSpeak = (dontAskAgain: boolean) => {
    if (dontAskAgain) { try { localStorage.setItem(VOICE_CONFIRM_OFF, '1'); } catch { /* ignore */ } }
    setConfirmingSpeak(false);
    void speak();
  };
  // Read the message aloud in the agent's voice. First play generates + caches (charged); replays are free.
  // The tricky bit: the audio URL only arrives after an async round-trip (dynamic import + TTS generation
  // + getDownloadURL), and a browser blocks play() that happens outside the original user gesture. So we
  // reuse ONE persistent <audio> element and UNLOCK it synchronously in the click (a muted play() primes
  // it); once unlocked, the later play() with the real URL is allowed even after the awaits.
  const speak = async () => {
    if (!conversationId || speaking) return;
    setSpeaking(true);
    let audio = audioRef.current;
    if (!audio) { audio = new Audio(); audioRef.current = audio; }
    // Clear any stale handlers from a previous play so the priming play() / a late event from the last
    // run can't fire against THIS invocation (premature spinner-off or a false error toast).
    audio.onended = null; audio.onerror = null;
    // Unlock inside the gesture: play a silent clip now so the element is user-activated; the later
    // play() with the real (async-fetched) URL is then allowed even though it happens after awaits.
    try { audio.src = SILENT_WAV; audio.muted = true; void audio.play().catch(() => { /* best-effort */ }); } catch { /* ignore */ }
    try {
      const { speakConversationMessage } = await import('../../services/conversationService');
      const { voicePath } = await speakConversationMessage(conversationId, msg.id);
      const url = await resolveDownloadUrl(voicePath);
      audio.pause();
      audio.muted = false;
      audio.src = url;
      // Idempotent stop + a watchdog so a stalled stream (backgrounded tab, network hang) can never leave
      // the spinner stuck forever with the button disabled.
      let done = false;
      const finish = () => { if (done) return; done = true; setSpeaking(false); };
      audio.onended = finish;
      audio.onerror = () => { finish(); showError('Could not play voice', 'The audio failed to load. Please try again.'); };
      await audio.play();
      window.setTimeout(finish, 120_000);
    } catch (e: any) {
      setSpeaking(false);
      const m = String(e?.message || e?.name || '');
      if (/NOT_ENOUGH_CREDITS|NOT_ENOUGH_MP/i.test(m)) showError('Out of credits', 'Top up to have agents speak.');
      else if (/isn.?t available|failed-precondition/i.test(m)) showError('Voice unavailable', 'Voice is off right now. Try again later.');
      else if (/NotAllowed/i.test(m)) showError('Tap to play', 'Tap the speaker again to hear it.');
      else showError('Could not play voice', 'Something went wrong. Please try again.');
    }
  };

  // Downloadable generated/uploaded media on this message (so users can SAVE it before it expires).
  const mediaAtts = (msg.attachments || []).filter((a) => INLINE_MEDIA.includes(a.kind) && (a.storage_path || a.drive_file_id));
  const canDownload = mediaAtts.length > 0;
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      for (const a of mediaAtts) {
        let url: string;
        if (a.drive_file_id && conversationId) {
          const { fetchDriveMediaUrl } = await import('../../services/conversationMediaService');
          url = await fetchDriveMediaUrl(conversationId, a.drive_file_id);
        } else {
          url = await resolveDownloadUrl(a.storage_path as string);
        }
        const blob = await fetchAsBlob(url);
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = a.name || 'download';
        document.body.appendChild(link); link.click(); link.remove();
        URL.revokeObjectURL(link.href);
      }
    } catch { /* best-effort */ } finally { setDownloading(false); }
  };
  return (
    <div className="flex gap-3" data-testid={`msg-agent-${msg.author}`}>
      {/* Avatar with the agent NAME stacked directly beneath it — identity grouped with the face. */}
      <div className="shrink-0 flex flex-col items-center gap-1 w-12">
        <button
          type="button"
          disabled={!canSelect}
          onClick={() => canSelect && onSelectAgent!(member!)}
          className="rounded-full disabled:cursor-default"
          data-testid="msg-avatar"
          title={canSelect ? 'View agent' : undefined}
          aria-label={canSelect ? `${displayName} details` : undefined}
        >
          {fromHuman
            ? <span className="w-8 h-8 rounded-full bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center"><User className="w-4 h-4 text-secondary-600 dark:text-secondary-300" /></span>
            : <MemberAvatar avatarIcon={member?.avatar_icon || msg.avatarIcon} avatarPath={member?.avatar_path} avatarDriveFileId={member?.avatar_drive_file_id} avatarImage={avatarImage} conversationId={conversationId} name={displayName} className="w-8 h-8" />}
        </button>
        <span className={`w-full text-center ${TYPE.micro} font-medium leading-tight text-secondary-500 dark:text-secondary-400 truncate`} title={displayName}>{displayName}</span>
      </div>
      <div className="max-w-[75%] space-y-1">
        <div className="flex items-center gap-2">
          {lens && <span className={`inline-flex items-center rounded-full bg-secondary-100 dark:bg-secondary-800 px-2 py-0.5 ${TYPE.micro} font-medium text-secondary-500 dark:text-secondary-400`} title={`${displayName}'s lens: ${member?.persona}`}>{lens}</span>}
          {/* A timestamp is `meta` on BOTH bubbles — the two sides of one conversation were reading 10px and 12px. */}
          {msg.ts ? <p className={`${TYPE.meta} font-normal text-secondary-400`} title={new Date(msg.ts).toLocaleString()}>{formatMsgTime(msg.ts)}</p> : null}
        </div>
        {/* The same deliberate 13px as the user's bubble (owner 2026-08-22). It only started applying on
            2026-08-23: `Markdown` used to declare `text-sm` on its own wrapper, which won over this class
            and rendered every specialist reply a step LARGER than the user's own words beside it. */}
        <div className="rounded-2xl rounded-tl-sm px-4 py-2.5 bg-secondary-100 dark:bg-secondary-800 text-[13px] leading-relaxed text-secondary-900 dark:text-secondary-100">
          <Markdown content={msg.content} memberNames={memberNames} onMention={onMention} />
        </div>
        {msg.citations && msg.citations.length > 0 && (
          <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 pt-0.5 ${TYPE.meta} text-secondary-400`} data-testid="msg-citations">
            <span className="inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> Sources</span>
            {msg.citations.slice(0, 8).map((c, i) => (
              <a key={i} href={c.url} target="_blank" rel="noopener noreferrer" title={c.title || c.url}
                className="inline-flex items-center gap-0.5 underline decoration-secondary-300 hover:text-secondary-700 dark:hover:text-secondary-200 max-w-[13rem] truncate">
                <span className="tabular-nums">{i + 1}.</span> {citationLabel(c)}
              </a>
            ))}
          </div>
        )}
        {canTranslate && (
          <div className={`${TYPE.control}`}>
            {!txOpen ? (
              <button type="button" onClick={translate} disabled={txLoading} className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300">
                {txLoading ? 'Translating…' : 'See translation'}
              </button>
            ) : (
              <div className="rounded-xl border border-secondary-200 dark:border-secondary-600 px-3 py-2 mt-0.5">
                <p className={`${TYPE.meta} text-secondary-400 mb-1`}>Translated to English</p>
                <p className="text-secondary-700 dark:text-secondary-200 whitespace-pre-wrap">{txText}</p>
                <button type="button" onClick={() => setTxOpen(false)} className="mt-1 text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300">Show original</button>
              </div>
            )}
          </div>
        )}
        <AttachmentBlock msg={msg} tone="other" conversationId={conversationId} />
        {isConfirmSpendSpec(msg.spec)
          ? (onConfirmSpend ? <ConfirmSpendCard spec={msg.spec} onConfirm={onConfirmSpend} /> : null)
          : msg.spec ? <JsonRenderer spec={((txOpen && txSpec) ? txSpec : msg.spec) as UISpec} onAction={onAction} /> : null}
        {/* Once read-aloud has generated the voice-over, show a persistent WhatsApp-style player (replays are FREE —
            the audio is stored, so no re-charge / no confirm-spend). It replaces the "Read aloud" speaker button. */}
        {msg.voicePath && <VoiceMessageBubble voicePath={msg.voicePath} mime={msg.voiceMime} />}
        {/* All per-message actions on ONE horizontal row: speak, download, feedback, reactions, bookmark, delete. */}
        {(canSpeak || canDownload || feedbackKind || onReact || onBookmark || onDelete) && (
          <div className="flex flex-wrap items-center gap-1 pt-0.5" data-testid="msg-actions">
            {canDownload && (
              <Tooltip content="Download (saves before media expires)">
                <button type="button" onClick={download} disabled={downloading}
                  aria-label="Download"
                  className="p-1 rounded-md text-secondary-400 hover:bg-secondary-200 dark:hover:bg-secondary-700">
                  {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                </button>
              </Tooltip>
            )}
            {canDownload && conversationId && (
              <Tooltip content="Share a link to this media">
                <button type="button" onClick={() => setShareOpen(true)}
                  aria-label="Share"
                  className="p-1 rounded-md text-secondary-400 hover:bg-secondary-200 dark:hover:bg-secondary-700">
                  <Share2 className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
            {canDownload && onRegenerate && promptText && (
              <Tooltip content="Make another one (confirm first)">
                <button type="button" onClick={() => onRegenerate(promptText)}
                  aria-label="Regenerate"
                  className="p-1 rounded-md text-secondary-400 hover:bg-secondary-200 dark:hover:bg-secondary-700">
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
            {canSpeak && !confirmingSpeak && !msg.voicePath && (
              <Tooltip content="Read aloud (replays are free)">
                <button type="button" onClick={onSpeakClick} disabled={speaking}
                  aria-label="Read aloud"
                  className={`p-1 rounded-md hover:bg-secondary-200 dark:hover:bg-secondary-700 ${speaking ? 'text-secondary-900 dark:text-white' : 'text-secondary-400'}`}>
                  {speaking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
              </Tooltip>
            )}
            {canSpeak && confirmingSpeak && !msg.voicePath && canAffordVoice && (
              // Inline spend-confirm — shows what will be spent before generating (mirrors the media confirm).
              <span className={`inline-flex items-center gap-1 rounded-md bg-secondary-100 dark:bg-secondary-800 px-1.5 py-0.5 ${TYPE.meta} text-secondary-600 dark:text-secondary-300`}>
                <Volume2 className="w-3 h-3" /> Read aloud · ~{voiceWpEstimate} WP
                <button type="button" onClick={() => confirmSpeak(false)} aria-label="Confirm read aloud" title="Read aloud"
                  className="ml-0.5 p-0.5 rounded text-secondary-900 dark:text-white hover:bg-secondary-200 dark:hover:bg-secondary-700"><Check className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => confirmSpeak(true)} aria-label="Read aloud and don't ask again" title="Read aloud + don't ask again"
                  className={`p-0.5 rounded text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 hover:bg-secondary-200 dark:hover:bg-secondary-700 ${TYPE.micro}`}>always</button>
                <button type="button" onClick={() => setConfirmingSpeak(false)} aria-label="Cancel"
                  className="p-0.5 rounded text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 hover:bg-secondary-200 dark:hover:bg-secondary-700"><X className="w-3.5 h-3.5" /></button>
              </span>
            )}
            {canSpeak && confirmingSpeak && !msg.voicePath && !canAffordVoice && (
              // Not enough credits for the estimate — guide to a top-up instead of failing into a toast.
              <span className={`inline-flex items-center gap-1 rounded-md bg-secondary-100 dark:bg-secondary-800 px-1.5 py-0.5 ${TYPE.meta} text-secondary-600 dark:text-secondary-300`}>
                <Volume2 className="w-3 h-3" /> Needs ~{voiceWpEstimate} WP
                <Link to="/wallet" onClick={() => setConfirmingSpeak(false)} title="Top up credits"
                  className="ml-0.5 rounded px-1 py-0.5 font-medium text-secondary-900 dark:text-white hover:bg-secondary-200 dark:hover:bg-secondary-700">Top up</Link>
                <button type="button" onClick={() => setConfirmingSpeak(false)} aria-label="Cancel"
                  className="p-0.5 rounded text-secondary-400 hover:text-secondary-700 dark:hover:text-secondary-200 hover:bg-secondary-200 dark:hover:bg-secondary-700"><X className="w-3.5 h-3.5" /></button>
              </span>
            )}
            {feedbackKind && (
              <>
                <Tooltip content="Helpful">
                  <button type="button" onClick={() => sendFeedback('up')} aria-label="Helpful"
                    className={`p-1 rounded-md hover:bg-secondary-200 dark:hover:bg-secondary-700 ${feedback === 'up' ? 'text-secondary-900 dark:text-white' : 'text-secondary-400'}`}>
                    <ThumbsUp className="w-3.5 h-3.5" fill={feedback === 'up' ? 'currentColor' : 'none'} />
                  </button>
                </Tooltip>
                <Tooltip content="Not helpful">
                  <button type="button" onClick={() => sendFeedback('down')} aria-label="Not helpful"
                    className={`p-1 rounded-md hover:bg-secondary-200 dark:hover:bg-secondary-700 ${feedback === 'down' ? 'text-secondary-900 dark:text-white' : 'text-secondary-400'}`}>
                    <ThumbsDown className="w-3.5 h-3.5" fill={feedback === 'down' ? 'currentColor' : 'none'} />
                  </button>
                </Tooltip>
              </>
            )}
            {onReact && <MessageReactions reactions={msg.reactions} myId={myUid} align="start" onToggle={onReact} />}
            {onBookmark && (
              <Tooltip content={msg.bookmarked ? 'Bookmarked — tap to remove' : 'Bookmark this message'}>
                <button type="button" onClick={() => onBookmark(!msg.bookmarked)}
                  aria-label={msg.bookmarked ? 'Remove bookmark' : 'Bookmark message'}
                  className={`p-1 rounded-md hover:bg-secondary-200 dark:hover:bg-secondary-700 ${msg.bookmarked ? 'text-secondary-900 dark:text-white' : 'text-secondary-400'}`}>
                  <Bookmark className="w-3.5 h-3.5" fill={msg.bookmarked ? 'currentColor' : 'none'} />
                </button>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip content="Delete this message">
                <button type="button" onClick={onDelete} aria-label="Delete message"
                  className="p-1 rounded-md text-secondary-400 hover:text-secondary-500 hover:bg-secondary-200 dark:hover:bg-secondary-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {shareOpen && conversationId && (
        <ShareMediaModal open={shareOpen} conversationId={conversationId} messageId={msg.id} onClose={() => setShareOpen(false)} />
      )}
    </div>
  );
};

export default MessageBubble;
